import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Spin } from "antd";
import { sendChatMessageWithConfig, getConfigsByType } from "../../api/chatbot/ChatbotApi";
import { usePermissions } from "../../hooks/usePermissions";
import { buildAdvisorContext } from "../../utils/buildAdvisorContext";
import { detectPlanQuery } from "../../utils/detectPlanQuery";
import SearchTopLevelPlansApi from "../../api/plans/SearchTopLevelPlansApi";
import SafeMarkdown from "../SafeMarkdown";
import { getMarkdownComponents } from "../../utils/markdownComponents";
import ChartRenderer from "../Chatbot/ChartRenderer";
import { ChartParser } from "../../utils/ChartParser";
import stratLogo from "../../assets/images/logo.png";

const DARK = "#0d1b2a";
const DARKER = "#091525";
const CARD_BG = "#112236";
const ACCENT = "#F15A24";
const BORDER = "rgba(241,90,36,0.35)";
const TEXT_DIM = "#94a3b8";

const SUGGESTIONS = [
  { title: "How is the performance cycle progressing?", label: "REQUEST ADVISOR" },
  { title: "Which strategic objectives need attention?", label: "REQUEST ADVISOR" },
  { title: "Who is performing best and worst this cycle?", label: "REQUEST ADVISOR" },
  { title: "Draft a Board briefing on overall progress", label: "REQUEST ADVISOR" },
];

// ── GEM icon — uses Strategizer logo ─────────────────────────────────────────
function GemIcon({ size = 28 }) {
  return (
    <div style={{
      width: size, height: size,
      borderRadius: Math.round(size * 0.32) + "px",
      background: `linear-gradient(135deg, ${ACCENT}, #c0390f)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, overflow: "hidden",
    }}>
      <img
        src={stratLogo}
        alt="GEM"
        style={{ width: size * 0.62, height: size * 0.62, objectFit: "contain", filter: "brightness(0) invert(1)" }}
      />
    </div>
  );
}

// ── Collapsed bar — taller, input at bottom ───────────────────────────────────
function CollapsedBar({ userName, onExpand, inputValue, setInputValue }) {
  return (
    <div style={{
      background: DARK,
      borderRadius: "16px",
      border: `1px solid ${BORDER}`,
      padding: "18px 20px 16px",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      minHeight: "120px",
    }}>
      {/* Brand row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <GemIcon size={34} />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#fff" }}>AI Executive Strategy Advisor</span>
              <span style={{
                fontSize: "9px", fontWeight: 700, color: ACCENT,
                border: `1px solid ${ACCENT}`, borderRadius: "3px",
                padding: "1px 4px", letterSpacing: "0.05em",
              }}>GEM</span>
            </div>
            <div style={{ fontSize: "11px", color: TEXT_DIM, marginTop: "1px" }}>
              Dynamic advisory calibrated for <strong style={{ color: "#cbd5e1" }}>{userName}</strong>
            </div>
          </div>
        </div>
        {/* Expand arrow */}
        <button
          onClick={() => onExpand(null)}
          style={{
            width: "32px", height: "32px", borderRadius: "8px",
            background: "rgba(255,255,255,0.06)", border: `1px solid rgba(255,255,255,0.1)`,
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      </div>

      {/* Suggestion chips */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "10px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.08em", alignSelf: "center" }}>
          Suggested:
        </span>
        {SUGGESTIONS.map((s, i) => (
          <button
            key={i}
            onClick={() => onExpand(s.title)}
            style={{
              fontSize: "11px", color: "#cbd5e1",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "20px", padding: "4px 12px",
              cursor: "pointer", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#cbd5e1"; }}
          >
            {s.title.length > 42 ? s.title.substring(0, 42) + "…" : s.title}
          </button>
        ))}
      </div>

      {/* Input at bottom */}
      <div style={{
        display: "flex", alignItems: "center",
        background: "rgba(255,255,255,0.04)",
        borderRadius: "50px",
        padding: "6px 6px 6px 18px",
        gap: "8px",
        border: "1px solid rgba(255,255,255,0.06)",
      }}>
        <input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && inputValue.trim()) onExpand(inputValue); }}
          placeholder='Ask AI Advisor (e.g., "Analyze bottlenecks")...'
          style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#fff", fontSize: "13px", caretColor: ACCENT }}
        />
        <style>{`.advisor-input::placeholder { color: #475569; }`}</style>
        <button
          onClick={() => inputValue.trim() ? onExpand(inputValue) : onExpand(null)}
          style={{
            width: "36px", height: "36px", borderRadius: "50%",
            background: inputValue.trim() ? ACCENT : "#1e3a5f",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, transition: "background 0.15s",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <line x1="22" y1="2" x2="11" y2="13" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" fill="#fff" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Expanded view ─────────────────────────────────────────────────────────────
function ExpandedView({ userName, messages, loading, onSend, onCollapse, onClear }) {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input);
    setInput("");
  };

  return (
    <div style={{
      background: DARK,
      borderRadius: "16px",
      border: `1px solid ${BORDER}`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minHeight: "500px",
      maxHeight: "620px",
    }}>
      {/* ── Header — brand + controls only, NO input here ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 18px",
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        background: DARKER,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <GemIcon size={30} />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#fff" }}>AI Executive Strategy Advisor</span>
              <span style={{
                fontSize: "9px", fontWeight: 700, color: ACCENT,
                border: `1px solid ${ACCENT}`, borderRadius: "3px",
                padding: "1px 4px",
              }}>GEM</span>
            </div>
            <div style={{ fontSize: "11px", color: TEXT_DIM, marginTop: "1px" }}>
              Dynamic advisory calibrated for <strong style={{ color: "#cbd5e1" }}>{userName}</strong>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* Clear */}
          <button onClick={onClear} title="Clear conversation" style={{
            width: "30px", height: "30px", borderRadius: "8px",
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
            color: TEXT_DIM, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />
            </svg>
          </button>
          {/* Collapse */}
          <button onClick={onCollapse} style={{
            width: "30px", height: "30px", borderRadius: "8px",
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Messages / empty state ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", scrollbarWidth: "none" }}>
        <style>{`.advisor-msgs::-webkit-scrollbar{display:none}`}</style>

        {/* Empty state */}
        {messages.length === 0 && !loading && (
          <div style={{ textAlign: "center", paddingTop: "32px" }}>
            <GemIcon size={56} style={{ margin: "0 auto 20px", borderRadius: "16px" }} />
            <div style={{ fontSize: "17px", fontWeight: 600, color: "#fff", marginBottom: "8px" }}>
              What strategic analysis or executive brief do you require today?
            </div>
            <div style={{ fontSize: "13px", color: TEXT_DIM, maxWidth: "440px", margin: "0 auto 28px", lineHeight: 1.6 }}>
              Connected to active dashboard indicators. Ask for execution SWOTs, compliance audits, or bottleneck modeling.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", maxWidth: "680px", margin: "0 auto" }}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => onSend(s.title)} style={{
                  background: CARD_BG, border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "12px", padding: "14px", textAlign: "left", cursor: "pointer", transition: "border-color 0.15s",
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = ACCENT}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"}
                >
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#fff", marginBottom: "10px", lineHeight: 1.4 }}>{s.title}</div>
                  <div style={{ fontSize: "10px", color: ACCENT, fontWeight: 700, letterSpacing: "0.06em" }}>
                    {s.label} →
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => (
          <div key={i} style={{ marginBottom: "18px" }}>
            {msg.role === "user" ? (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{
                  maxWidth: "72%", background: ACCENT, color: "#fff",
                  padding: "11px 16px", borderRadius: "16px 16px 4px 16px",
                  fontSize: "13px", lineHeight: "1.6",
                }}>
                  {msg.content}
                  <div style={{ fontSize: "10px", opacity: 0.65, marginTop: "5px", textAlign: "right" }}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <GemIcon size={28} style={{ marginTop: "2px", borderRadius: "8px" }} />
                <div style={{
                  flex: 1, minWidth: 0, background: CARD_BG,
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "4px 12px 12px 12px", padding: "12px 16px",
                }}>
                  {msg.isLoading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ fontSize: "12px", color: ACCENT, fontFamily: "monospace" }}>Analyzing dashboard KPIs...</span>
                      <Spin size="small" />
                    </div>
                  ) : (() => {
                    const parsed = ChartParser.parse(msg.content);
                    return (
                      <>
                        <SafeMarkdown components={getMarkdownComponents({ primary: ACCENT, text: "#e2e8f0", headings: "#fff" })}>
                          {parsed.text || msg.content}
                        </SafeMarkdown>
                        {parsed.hasChart && (
                          <div style={{ marginTop: "14px" }}>
                            <ChartRenderer type={parsed.type} data={parsed.data} config={{ width: 560, height: 300 }} />
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {!msg.isLoading && (
                    <div style={{ fontSize: "10px", color: "#475569", marginTop: "5px" }}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {msg.model && <span style={{ marginLeft: "5px" }}>· {msg.model}</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Input — always at bottom, pill style ── */}
      <div style={{
        padding: "10px 18px 14px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        background: DARKER,
        flexShrink: 0,
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          background: "rgba(255,255,255,0.04)",
          borderRadius: "50px",
          padding: "6px 6px 6px 18px",
          gap: "8px",
        }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleSend(); }}
            placeholder="Ask a follow-up or specify strategic details..."
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "#e2e8f0", fontSize: "13px", caretColor: ACCENT,
            }}
          />
          <button onClick={handleSend} style={{
            width: "36px", height: "36px", borderRadius: "50%",
            background: input.trim() ? ACCENT : "#1e3a5f",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, transition: "background 0.15s",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <line x1="22" y1="2" x2="11" y2="13" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" fill="#fff" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function AIExecutiveAdvisor() {
  const { hasGemPermission } = usePermissions();
  const userState = useSelector(s => s.userState.value);
  const positionState = useSelector(s => s.positionState.value);
  const perfCycleState = useSelector(s => s.perfCycleState.value);

  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gemConfig, setGemConfig] = useState(null);
  const [collapsedInput, setCollapsedInput] = useState("");
  const [lastResolvedPlan, setLastResolvedPlan] = useState(null); // { id, title } — follow-up tracking

  // Must be before any early return
  const advisorRef = useRef(null);

  // Load GEM config on mount
  useEffect(() => {
    (async () => {
      try {
        const configs = await getConfigsByType("GEM");
        if (configs?.length > 0) setGemConfig(configs[0]);
      } catch { /* silent */ }
    })();
  }, []);

  if (!hasGemPermission) return null;

  const userName = `${userState?.firstName || ""} ${userState?.lastName || ""}`.trim() || "User";

  // Build rich dashboard context using shared utility
  const buildContextPrefix = async () => {
    return buildAdvisorContext({
      positionId: positionState?.id,
      perfCycleId: perfCycleState?.id,
      cycleName: perfCycleState?.name,
      cycleStart: perfCycleState?.startingOn,
      cycleEnd: perfCycleState?.endingOn,
    });
  };

  const getFollowUpType = (text) => {
    const lower = text.toLowerCase();
    if (/cascade|child plan|sub plan|cascade plan|breakdown|children/.test(lower)) return 'cascade';
    if (/split plan|split plans|splits\b/.test(lower)) return 'split';
    if (/\btasks?\b|task progress|task status/.test(lower)) return 'tasks';
    if (/\bkpis?\b|key performance/.test(lower)) return 'kpis';
    return 'general';
  };

  const PLAN_FOLLOWUP_KEYWORDS = [
    'cascade', 'child plan', 'sub plan', 'split plan', 'split plans',
    'cascade plan', 'cascade plans', 'breakdown', 'children',
    'tasks', 'task progress', 'task status',
    'kpi', 'kpis', 'key performance',
    'this plan', 'same plan', 'tell me more', 'more details',
    'what about', 'and what', 'also', 'additionally',
    'who owns', 'owner', 'owners',
  ];

  const isFollowUpAboutPlan = (text) => {
    if (!lastResolvedPlan) return false;
    const lower = text.toLowerCase();
    return PLAN_FOLLOWUP_KEYWORDS.some(k => lower.includes(k));
  };

  const sendMessage = async (text) => {
    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    const loadingMsg = { role: "assistant", content: "", isLoading: true, timestamp: new Date().toISOString() };

    setMessages(prev => [...prev, userMsg, loadingMsg]);
    setExpanded(true);
    setLoading(true);

    try {
      // ── Step 0: Follow-up about last resolved plan ──
      if (isFollowUpAboutPlan(text) && lastResolvedPlan) {
        const followUpType = getFollowUpType(text);
        const directives = {
          cascade: `[INSTRUCTION: CASCADE PLAN DEEP-DIVE. DO NOT repeat the parent plan status report. List EACH cascade plan individually — performance, target with unit symbol, owner, one specific action per underperforming plan.]`,
          split: `[INSTRUCTION: SPLIT PLAN DEEP-DIVE. DO NOT repeat the parent plan status report. Analyse EACH split plan — title, owner, performance%, target with unit, achieved vs target.]`,
          tasks: `[INSTRUCTION: List all TASKS — name, status, assigned to, dates. Highlight overdue incomplete tasks.]`,
          kpis: `[INSTRUCTION: List all KPIs — name, target, achieved, performance%. Flag any below 50% as at risk.]`,
          general: `[INSTRUCTION: Answer this follow-up from the plan data in context. Do not repeat the full status report.]`,
        };
        const directive = directives[followUpType] || directives.general;
        const enrichedText = `#${lastResolvedPlan.id}:${lastResolvedPlan.title}\n\n${directive}\n\nUser question: ${text}`;
        const response = gemConfig
          ? await sendChatMessageWithConfig(enrichedText, null, gemConfig.id, "GPT")
          : await import("../../api/chatbot/ChatbotApi").then(m => m.sendChatMessage(enrichedText, null, "GPT"));
        setMessages(prev => [
          ...prev.slice(0, -1),
          { role: "assistant", content: response.reply, timestamp: new Date().toISOString(), model: response.model },
        ]);
        setLoading(false);
        return;
      }

      // ── Step 1: Check if user is asking about a specific plan by name ──
      if (perfCycleState?.id) {
        const planQuery = await detectPlanQuery(text, perfCycleState.id, perfCycleState.name);

        if (planQuery.type === 'not_found') {
          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: planQuery.notFoundMessage, timestamp: new Date().toISOString() },
          ]);
          setLoading(false);
          return;
        }

        if (planQuery.type === 'ambiguous') {
          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: planQuery.clarificationMessage, timestamp: new Date().toISOString() },
          ]);
          setLoading(false);
          return;
        }

        if (planQuery.type === 'single') {
          const plan = planQuery.plan;
          setLastResolvedPlan({ id: plan.id, title: plan.title });
          const enrichedText = `#${plan.id}:${plan.title}\n\n${text}`;
          const response = gemConfig
            ? await sendChatMessageWithConfig(enrichedText, null, gemConfig.id, "GPT")
            : await import("../../api/chatbot/ChatbotApi").then(m => m.sendChatMessage(enrichedText, null, "GPT"));
          setMessages(prev => [
            ...prev.slice(0, -1),
            { role: "assistant", content: response.reply, timestamp: new Date().toISOString(), model: response.model },
          ]);
          setLoading(false);
          return;
        }

        // ── Step 1b: Fallback — detectPlanQuery didn't match a pattern, but the
        // message may still NAME a specific plan (e.g. "<Plan Name> analysis on
        // this plan"). Try a direct search so a single-plan question isn't
        // answered with cycle-wide context. Cycle/aggregate questions are
        // excluded and fall through to the cycle context below.
        const isCycleLevelQuery = /\b(how\s+are\s+we|how'?s\s+(?:the\s+)?(?:cycle|performance|it\s+going)|cycle|overall|organisation|organization|all\s+plans|my\s+plans|which\s+plans|what\s+plans|list\s+plans|show\s+plans|worst\s+performing|best\s+performing|top\s+performer|who\s+(?:is|are|needs)|board\s+brief|briefing|employee|team\s+member|attention\s+required|needs\s+attention)\b/i.test(text);

        if (planQuery.type === 'no_match' && text.trim().length >= 10 && !/#\d+\s*:/.test(text) && !isCycleLevelQuery) {
          const directQuery = text.trim()
            .replace(/^(tell\s+me\s+about|what\s+about|show\s+me|give\s+me|how\s+is|how'?s|progress\s+on|status\s+of|overview\s+of|update\s+on|analysis\s+(?:on|of|for)|analyse|analyze)\s+/gi, '')
            .replace(/(\s+(analysis|analyse|analyze|overview|update|progress|status|review|report|details?|info|summary|please|now|tell\s+me|about\s+(?:this|it)|for\s+me|on\s+this\s+plan|this\s+plan))+\s*[?.!]*$/gi, '')
            .trim();

          if (directQuery.length >= 6) {
            const directResult = await SearchTopLevelPlansApi(directQuery, perfCycleState.id);
            if (directResult?.success && directResult.data?.length === 1) {
              const plan = directResult.data[0];
              setLastResolvedPlan({ id: plan.id, title: plan.title });
              const enrichedText = `#${plan.id}:${plan.title}\n\n${text}`;
              const response = gemConfig
                ? await sendChatMessageWithConfig(enrichedText, null, gemConfig.id, "GPT")
                : await import("../../api/chatbot/ChatbotApi").then(m => m.sendChatMessage(enrichedText, null, "GPT"));
              setMessages(prev => [
                ...prev.slice(0, -1),
                { role: "assistant", content: response.reply, timestamp: new Date().toISOString(), model: response.model },
              ]);
              setLoading(false);
              return;
            }
            if (directResult?.success && directResult.data?.length > 1) {
              const listText = directResult.data
                .map((p, i) => `${i + 1}. **${p.title}**${p.planLevel ? ` _(${p.planLevel})_` : ''}`)
                .join('\n');
              setMessages(prev => [
                ...prev.slice(0, -1),
                { role: "assistant", content: `I found ${directResult.data.length} plans that might match:\n\n${listText}\n\nWhich one would you like to know about? Reply with the number.`, timestamp: new Date().toISOString() },
              ]);
              setLoading(false);
              return;
            }
          }
        }
      }

      // ── Step 2: Normal flow — always enrich home advisor with cycle context ──
      const ctx = await buildContextPrefix();
      const fullMessage = ctx ? ctx + '\n\n' + text : text;

      const response = gemConfig
        ? await sendChatMessageWithConfig(fullMessage, null, gemConfig.id, "GPT")
        : await import("../../api/chatbot/ChatbotApi").then(m => m.sendChatMessage(fullMessage, null, "GPT"));

      setMessages(prev => [
        ...prev.slice(0, -1), // remove loading
        {
          role: "assistant",
          content: response.reply,
          timestamp: new Date().toISOString(),
          model: response.model,
        },
      ]);
    } catch (e) {
      setMessages(prev => [
        ...prev.slice(0, -1),
        { role: "assistant", content: "Sorry, I encountered an error. Please try again.", timestamp: new Date().toISOString() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = (initialMessage) => {
    setExpanded(true);
    setCollapsedInput("");
    // Scroll the widget into view without jumping the whole page
    setTimeout(() => {
      advisorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    if (initialMessage?.trim()) {
      sendMessage(initialMessage.trim());
    }
  };

  if (!expanded) {
    return (
      <div ref={advisorRef} style={{ marginBottom: "24px" }}>
        <CollapsedBar
          userName={userName}
          onExpand={handleExpand}
          inputValue={collapsedInput}
          setInputValue={setCollapsedInput}
        />
      </div>
    );
  }

  return (
    <div ref={advisorRef} style={{ marginBottom: "24px" }}>
      <ExpandedView
        userName={userName}
        messages={messages}
        loading={loading}
        onSend={sendMessage}
        onCollapse={() => setExpanded(false)}
        onClear={() => setMessages([])}
      />
    </div>
  );
}
